import React, { useEffect, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import * as FiIcons from 'react-icons/fi';
import DataModeBadge from '../../components/feedback/DataModeBadge.jsx';
import { listAllClientRequests } from '../../lib/api/agencyRequests';

const STATUS_TONES = {
  in_review: 'var(--status-autonomous)',
  routed: 'var(--status-queued)',
  submitted: 'var(--status-queued)',
  resolved: 'var(--status-running)',
  cancelled: 'var(--text-secondary-ops)'
};

/**
 * "Client requests" feed (full-live-cutover §3.6 — the §2→§3 integration
 * surface). Reads GET /api/clients/requests (agency roles only): every
 * tenant's portal submissions, newest first, each with its clientId so ops
 * can see WHO filed it. Fetch-first with the honest labeled fallback — a
 * portal submission lands here within the same session it is filed.
 */
function ClientRequestsFeed() {
  // null = loading; [] = live-empty; array = live rows.
  const [requests, setRequests] = useState(null);
  const [mode, setMode] = useState('live');

  useEffect(() => {
    let cancelled = false;
    listAllClientRequests()
      .then((rows) => {
        if (cancelled) return;
        setRequests(Array.isArray(rows) ? rows : []);
        setMode('live');
      })
      .catch(() => {
        // 401/403/network — no fabricated rows, just the honest state.
        if (cancelled) return;
        setRequests(null);
        setMode('sample');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section className="panel client-requests-feed">
      <div className="section-heading">
        <div>
          <h2>
            Client requests{' '}
            <DataModeBadge
              mode={mode}
              label={mode === 'live' ? null : 'Live feed unreachable — portal submissions not shown'}
            />
          </h2>
          <span>Portal submissions from every client tenant, newest first (dry-run routing).</span>
        </div>
        <span className="console-page__count">
          {requests === null ? '' : `${requests.length} recent`}
        </span>
      </div>

      {requests === null ? (
        <p className="event-empty">
          {mode === 'sample'
            ? 'The requests feed is not reachable from this session — no sample rows are shown for live submissions.'
            : 'Loading requests…'}
        </p>
      ) : requests.length === 0 ? (
        <p className="event-empty">No portal submissions yet.</p>
      ) : (
        <div className="event-list">
          {requests.map((request) => (
            <div className="event-item" key={request.id}>
              <span
                className="event-item-icon"
                style={{ color: STATUS_TONES[request.status] || 'var(--text-secondary-ops)' }}
              >
                <FiIcons.FiInbox size={13} />
              </span>
              <div className="event-item-body">
                <strong>{request.clientId || 'unknown tenant'}</strong>
                <p>{request.subject}</p>
              </div>
              <time>
                {request.created
                  ? formatDistanceToNow(new Date(request.created), { addSuffix: true })
                  : (request.status || '').replace('_', ' ')}
              </time>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export default ClientRequestsFeed;
